// Canonical hot-sauce heat scale. One ordinal value (1-5) is stored per item
// (items.heat_level); every labeling system a seller might prefer — flame count,
// named tier, Scoville range — is derived from it here so the data always has an
// equivalent value in each. Single source of truth: change a band, change it once.

// Level 0 ("No heat") is a real, seller-chosen value for BBQ sauces, marinades,
// and other sauces with no spice. It is distinct from a null heat_level, which
// means "not yet rated" (what the one-time retro prompt looks for).
export type HeatLevel = 0 | 1 | 2 | 3 | 4 | 5;

export interface HeatTier {
  level: HeatLevel;
  name: string;
  scovilleMin: number;
  scovilleMax: number | null;
  scovilleLabel: string;
  peppers: string;
}

export const HEAT_TIERS: HeatTier[] = [
  { level: 0, name: "No heat", scovilleMin: 0, scovilleMax: 0, scovilleLabel: "0 SHU", peppers: "BBQ, marinades, sweet sauces" },
  { level: 1, name: "Mild", scovilleMin: 1, scovilleMax: 2500, scovilleLabel: "1–2,500 SHU", peppers: "poblano, pepperoncini" },
  { level: 2, name: "Medium", scovilleMin: 2500, scovilleMax: 25000, scovilleLabel: "2,500–25,000 SHU", peppers: "jalapeño, serrano" },
  { level: 3, name: "Hot", scovilleMin: 25000, scovilleMax: 100000, scovilleLabel: "25,000–100,000 SHU", peppers: "cayenne, tabasco, thai" },
  { level: 4, name: "Extra Hot", scovilleMin: 100000, scovilleMax: 350000, scovilleLabel: "100,000–350,000 SHU", peppers: "habanero, scotch bonnet" },
  { level: 5, name: "Extreme", scovilleMin: 350000, scovilleMax: null, scovilleLabel: "350,000+ SHU", peppers: "ghost, scorpion, reaper" },
];

export const HOT_SAUCE_SUBTYPE = "hot-sauce";
export const FLAME = "🌶️";

export function isHeatLevel(value: unknown): value is HeatLevel {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 5;
}

export function heatTier(level: number | null | undefined): HeatTier | null {
  return isHeatLevel(level) ? HEAT_TIERS.find((t) => t.level === level) ?? null : null;
}

export function heatFlames(level: number | null | undefined): string {
  return isHeatLevel(level) && level > 0 ? FLAME.repeat(level) : "";
}
